import { useRef } from 'react';
import Button from '@discover-ui/button';
import { DialogTrigger } from '@discover-ui/dialog';
import HowItWorksModal from '@/components/organisms/HowItWorksModal';
import { cn } from '@discover-lib/utils';
import useDragToScroll from '@discover-lib/useDragToScroll';
import DismissButton from '../dismissButton';
import { HOW_IT_WORKS_STEPS } from './steps';

export default function HowItWorksCarousel({ onDismiss }: { onDismiss: () => void }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isDragging, isScrollable, handleMouseDown, handleClickCapture } = useDragToScroll(
    scrollRef,
    { contentVersion: HOW_IT_WORKS_STEPS.length }
  );

  return (
    <section className="relative bg-gray-100/50 py-4 lg:hidden">
      <DismissButton label="Dismiss How It Works" onClick={onDismiss} />

      <div className="px-4 pr-12">
        <h2 className="text-base font-semibold leading-5 text-ds-gray-light-950">
          Enjoy 50% off your food bill!
        </h2>
        <p className="mt-1 text-xs leading-5 text-ds-gray-light-800">
          Every restaurant on First Table offers you 50% off food
        </p>

        <HowItWorksModal
          trackingSource="cr158_carousel"
          renderTrigger={
            <DialogTrigger asChild>
              <Button
                kind="no-style"
                type="button"
                className="mt-1.5 rounded text-xs font-semibold leading-5 text-ds-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                Learn more
              </Button>
            </DialogTrigger>
          }
        />
      </div>

      <div
        ref={scrollRef}
        className={cn(
          'no-scrollbar mt-3 flex max-w-full flex-row items-stretch gap-2.5 overflow-x-auto px-4',
          isScrollable && isDragging && 'cursor-grabbing',
          isScrollable && !isDragging && 'cursor-grab'
        )}
        onMouseDownCapture={handleMouseDown}
        onClickCapture={handleClickCapture}>
        {HOW_IT_WORKS_STEPS.map((step, index) => (
          <article
            key={step.title}
            className="flex w-full max-w-[300px] shrink-0 items-start gap-2.5 rounded-lg border border-ds-gray-light-100 bg-ds-white p-4 shadow-sm">
            <span className="flex size-8 shrink-0 items-center justify-center rounded border border-ds-brand-200 p-2 font-serif text-sm leading-none text-ds-brand-700">
              {index + 1}
            </span>
            <div className="mt-0.5">
              <h3 className="text-xs font-semibold leading-5 text-ds-gray-light-800">
                {step.title}
              </h3>
              <p className="text-xs leading-5 text-ds-gray-light-700">{step.description}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
